import { signalRService } from './signalRService.ts';

class WebRTCService {
  private peerConnection: RTCPeerConnection | null = null;
  private localStream: MediaStream | null = null;
  private currentMatchId: string | null = null;
  private pendingCandidates: RTCIceCandidateInit[] = [];
  private isOfferer: boolean = false;

  private async setupPeerConnection() {
    if (this.peerConnection) {
      this.peerConnection.close();
    }

    const configuration = {
      iceServers: [
        // Google's public STUN server
        { urls: 'stun:stun.l.google.com:19302' },
        {
            urls: [
              'turn:116.202.111.237:3478',
              'turns:116.202.111.237:5349'
            ],
            username: 'wofm',
            credential: 'wofm123'
        }
      ],
      iceCandidatePoolSize: 50
    };

    this.peerConnection = new RTCPeerConnection(configuration);
    console.log('Created new peer connection');

    // Add local stream tracks to peer connection
    if (this.localStream) {
      this.localStream.getTracks().forEach(track => {
        if (this.peerConnection && this.localStream) {
          this.peerConnection.addTrack(track, this.localStream);
        }
      });
    }

    // Handle incoming remote stream
    this.peerConnection.ontrack = (event) => {
      console.log('Received remote track:', event.track.kind);
      const stream = event.streams[0];
      document.dispatchEvent(new CustomEvent('remoteStreamAvailable', { detail: { stream } }));
    };

    // Handle ICE candidates
    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate && this.currentMatchId) {
        signalRService.sendIceCandidate(this.currentMatchId, event.candidate);
      }
    };

    this.peerConnection.oniceconnectionstatechange = () => {
      console.log('ICE connection state:', this.peerConnection?.iceConnectionState);
    };

    this.peerConnection.onsignalingstatechange = () => {
      console.log('Signaling state:', this.peerConnection?.signalingState);
    };
  }

  private async addBufferedCandidates() {
    if (!this.peerConnection || !this.peerConnection.remoteDescription) {
      return;
    }

    while (this.pendingCandidates.length > 0) {
      const candidate = this.pendingCandidates.shift();
      if (candidate) {
        try {
          await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
          console.log('Added buffered ICE candidate');
        } catch (error) {
          console.error('Error adding buffered ICE candidate:', error);
        }
      }
    }
  }

  public async startCall(matchId: string, isUser1: boolean): Promise<MediaStream> {
    try {
      this.currentMatchId = matchId;
      this.pendingCandidates = [];
      this.isOfferer = isUser1;

      // Get local media stream if not already obtained
      if (!this.localStream) {
        this.localStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
      }
      if (this.isOfferer) {
        await this.setupPeerConnection();
        console.log('Creating offer as offerer...');
        const offer = await this.peerConnection!.createOffer();
        console.log('Setting local description (offer)...');
        await this.peerConnection!.setLocalDescription(offer);
        console.log('Sending offer...');
        await signalRService.sendOffer(this.currentMatchId!, offer);
      }

      // Create and send offer
      

      return this.localStream;
    } catch (error) {
      console.error('Error in startCall:', error);
      throw error;
    }
  }

  public async handleOffer(matchId: string, offer: RTCSessionDescriptionInit) {
    try {
      console.log('handleOffer');
      this.currentMatchId = matchId;
      this.pendingCandidates = [];
      this.isOfferer = false;

      // Get local media stream if not already obtained
      if (!this.localStream) {
        this.localStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: true
        });
      }

      await this.setupPeerConnection();

      console.log('Setting remote description (offer) as answerer...');
      await this.peerConnection!.setRemoteDescription(new RTCSessionDescription(offer));
      //await this.addBufferedCandidates();

      console.log('Creating answer as answerer...');
      const answer = await this.peerConnection!.createAnswer();
      console.log('Setting local description (answer)...');
      await this.peerConnection!.setLocalDescription(answer);
      console.log('Sending answer...');
      await signalRService.sendAnswer(matchId, answer);
    } catch (error) {
      console.error('Error in handleOffer:', error);
      //throw error;
    }
  }

  public async handleAnswer(answer: RTCSessionDescriptionInit) {
    try {
      if (!this.peerConnection) {
        console.warn('No peer connection when handling answer');
        return;
      }

      if (!this.isOfferer) {
        console.warn('Received answer but we are not the offerer');
        return;
      }

      console.log('Setting remote description (answer) as offerer...');
      await this.peerConnection.setRemoteDescription(new RTCSessionDescription(answer));
      //await this.addBufferedCandidates();
    } catch (error) {
      console.error('Error in handleAnswer:', error);
    }
  }

  public async handleIceCandidate(candidate: RTCIceCandidateInit) {
    try {
      if (!this.peerConnection) {
        console.warn('No peer connection when handling ICE candidate');
        return;
      }

      if (!candidate.sdpMid && !candidate.sdpMLineIndex) {
        console.warn('Skipping ICE candidate due to missing properties:', candidate);
        return;
      }

      await this.peerConnection.addIceCandidate(new RTCIceCandidate(candidate));
      console.log('Added ICE candidate immediately');
    } catch (error) {
      console.error('Error handling ICE candidate:', error);
    }
  }

  public endCall() {
    if (this.peerConnection) {
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.localStream) {
      this.localStream.getTracks().forEach(track => track.stop());
      this.localStream = null;
    }

    this.currentMatchId = null;
    this.pendingCandidates = [];
    this.isOfferer = false;
  }
}

export const webRTCService = new WebRTCService(); 